import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, clearToken, getErrorMessage, setToken } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  LoginChallengeResponseSchema,
  LoginResponseSchema,
  UserSchema,
  type LoginRequest,
  type User,
} from "@/api/types/auth";

// Opt-in 2FA: POST /v2/auth/login returns EITHER a normal LoginResponse
// (no 2FA — user is signed in) OR a {totp_required, challenge} response
// (2FA enrolled — caller must finish with useTwoFactorLogin). Discriminated
// so LoginPage can branch without guessing at the raw shape.
export type LoginOutcome = { totpRequired: true; challenge: string } | { totpRequired: false; user: User };

/**
 * Current-user query. Not gated on an in-memory token: the httpOnly cookie
 * fallback means a page reload can still be authenticated even though the
 * in-memory Bearer token was lost on refresh (contract: POST /auth/login
 * sets the cookie in addition to returning access_token in the body).
 */
export function useAuth() {
  const queryClient = useQueryClient();

  const {
    data: user,
    isLoading,
    error,
  } = useQuery<User>({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      const { data } = await apiClient.get("/v2/auth/me");
      return validateResponse(UserSchema, data, "GET /v2/auth/me");
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const logout = useCallback(async () => {
    try {
      await apiClient.post("/v2/auth/logout");
    } catch {
      // Ignore — clear client state regardless of server response.
    }
    clearToken();
    queryClient.clear();
    window.location.href = "/login";
  }, [queryClient]);

  return {
    user: user ?? null,
    isLoading,
    isAuthenticated: !!user && !error,
    role: user?.role ?? null,
    logout,
    getErrorMessage,
  };
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation<LoginOutcome, unknown, LoginRequest>({
    mutationFn: async (credentials) => {
      const { data } = await apiClient.post("/v2/auth/login", credentials);

      // 2FA-enrolled account: no token yet, just a challenge to hand to
      // POST /v2/auth/2fa/login. Checked BEFORE the normal-login parse
      // below so a non-2FA login's parse/setToken/return path is
      // byte-for-byte unchanged from before 2FA existed.
      const challengeCheck = LoginChallengeResponseSchema.safeParse(data);
      if (challengeCheck.success) {
        return { totpRequired: true, challenge: challengeCheck.data.challenge };
      }

      const parsed = validateResponse(
        LoginResponseSchema,
        data,
        "POST /v2/auth/login",
      );
      setToken(parsed.access_token);
      return { totpRequired: false, user: parsed.user };
    },
    onSuccess: (outcome) => {
      if (!outcome.totpRequired) {
        queryClient.setQueryData(["auth", "me"], outcome.user);
      }
    },
  });
}
